import { TestId } from '@components/test-ids';
import styled from '@emotion/styled';
import { Resort } from '@model/common';
import { boxShadowMixins, mixins } from '@styles/mixins';
import { Theme, ThemeProps } from '@theme/base';
import { withTheme } from '@emotion/react';
import React from 'react';
import { DestinationLink } from '@components/common/next-link';
import { LazyLoadBackgroundImage } from '@components/common/lazy-load';
import { useI18NextContext } from '@components/hooks';

export interface TileProps {
  theme?: Theme;
  resort: Resort;
  image: string;
}

export const DestinationTitleLabel = styled.h3(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  margin: 0,
  ...mixins(theme).truncate
}));

export const OuterContainer: any = styled.div(({ theme }: ThemeProps) => ({
  borderRadius: theme.custom.spacing.small,
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  boxShadow: boxShadowMixins(theme.custom.colors.group10.base).light,
  paddingTop: 250
}));

export const BackgroundImage: any = styled(LazyLoadBackgroundImage)(({ theme }: ThemeProps) => ({
  position: 'relative',
  backgroundColor: theme.custom.colors.group10.light,
  transition: 'all 0.5s ease',
  WebkitUserSelect: 'none' /* disable selection/Copy of UIWebView */,
  WebkitTouchCallout: 'none' /* disable the IOS popup when long-press on a link */,
  ':hover': {
    transform: 'scale(1.2)'
  }
}));

export const Label = styled.div(({ theme }: ThemeProps) => ({
  position: 'absolute',
  bottom: theme.custom.spacing.large,
  left: theme.custom.spacing.large,
  padding: 10,
  backgroundColor: theme.custom.colors.overlay.darker,
  ...(theme.custom.typography.h5 as any),
  color: theme.custom.colors.white
}));

export const HotelsCount = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any),
  color: theme.custom.colors.white,
  paddingLeft: theme.custom.spacing.xSmall
}));

export const TileComponent = ({ resort, image }: TileProps) => {
  const t: any = useI18NextContext();
  const { path, display, holidays } = resort;

  return (
    <DestinationLink path={path}>
      <OuterContainer data-testid={TestId.resortGroup.panel}>
        <BackgroundImage src={image} />
        <Label>
          <DestinationTitleLabel data-testid={TestId.destination.titleLabel}>
            {display}
            {!!holidays && (
              <HotelsCount>({t('explore-resorts__tiles--hotel-count', { count: holidays.length })})</HotelsCount>
            )}
          </DestinationTitleLabel>
        </Label>
      </OuterContainer>
    </DestinationLink>
  );
};

export const Tile = withTheme(TileComponent);
